/**
 * Feedback API service
 * Handles submitting user feedback to the server
 */

// API configuration from environment variables
const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

/**
 * Submit feedback to the server
 * @param {Object} feedbackData - The feedback form data
 * @param {string} feedbackData.category - 'bug', 'feature', or 'feedback'
 * @param {string} feedbackData.description - The feedback description
 * @param {string} [feedbackData.pageFeature] - The page/feature this relates to
 * @param {string} [feedbackData.email] - User's email for follow-up
 * @param {boolean} [feedbackData.contactRequested] - Whether user wants to be contacted
 * @param {File} [feedbackData.screenshot] - Optional screenshot file
 * @param {string} [feedbackData.userId] - User ID if logged in
 * @param {boolean} [feedbackData.isLoggedIn] - Whether user is logged in
 * @returns {Promise<Object>} - The response from the server
 */
export async function submitFeedback(feedbackData) {
    const formData = new FormData();

    // Add all text fields to FormData
    formData.append('category', feedbackData.category);
    formData.append('description', feedbackData.description);

    if (feedbackData.pageFeature) {
        formData.append('pageFeature', feedbackData.pageFeature);
    }

    if (feedbackData.email) {
        formData.append('email', feedbackData.email);
    }

    formData.append('contactRequested', feedbackData.contactRequested || false);
    formData.append('isLoggedIn', feedbackData.isLoggedIn || false);

    if (feedbackData.userId) {
        formData.append('userId', feedbackData.userId);
    }

    // Add metadata
    formData.append('submittedFrom', window.location.href);
    formData.append('userAgent', navigator.userAgent);
    formData.append('captchaToken', feedbackData.captchaToken);

    // Add screenshot if provided
    if (feedbackData.screenshot) {
        formData.append('screenshot', feedbackData.screenshot);
    }

    const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary for FormData
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.error || `HTTP error! status: ${response.status}`
        );
    }

    return await response.json();
}

export default { submitFeedback };
